import {Button, Input, message, Modal, Progress, Select, Typography} from "antd";
import React, {useState} from "react";
import axios from "axios";
import {getId} from "../../helpers/file";
import {addFailedFile} from "../../helpers/uploadState";

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// 简单的 EventBus 实现
class EventBus {
  private events: { [key: string]: Function[] } = {};

  on(event: string, callback: Function) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  emit(event: string, data?: any) {
    if (this.events[event]) {
      this.events[event].forEach(callback => callback(data));
    }
  }

  off(event: string, callback?: Function) {
    if (this.events[event]) {
      if (callback) {
        this.events[event] = this.events[event].filter(cb => cb !== callback);
      } else {
        delete this.events[event];
      }
    }
  }
}

// 创建全局 EventBus 实例
export const eventBus = new EventBus();

interface UploadProgress {
  url: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  message?: string;
}

interface UploadResult {
  totalCount: number;
  successCount: number;
  errorCount: number;
  successUrls: string[];
  errorUrls: string[];
  completedAt: Date | null;
}

const UrlUpload = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [urls, setUrls] = useState("");
  const [selectedDir, setSelectedDir] = useState("url_upload");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [randomPath, setRandomPath] = useState(false);
  
  // 新增：专门的上传结果状态
  const [uploadResult, setUploadResult] = useState<UploadResult>({
    totalCount: 0,
    successCount: 0,
    errorCount: 0,
    successUrls: [],
    errorUrls: [],
    completedAt: null
  });
  
  // 组件初始化时加载默认目录
  React.useEffect(() => {
    // 设置默认目录选项，直到从接口获取到真实数据
    setDirOptions([
      { value: "url_upload", label: "URL上传目录" }
    ]);
  }, []);

  // 目录选项，从接口动态获取
  const [dirOptions, setDirOptions] = useState<Array<{value: string, label: string}>>([]);
  const [isLoadingDirs, setIsLoadingDirs] = useState(false);

  // 获取目录列表
  const fetchDirectoryList = async () => {
    try {
      setIsLoadingDirs(true);
      const response = await axios.get('/img-dir/list');

      console.info('获取目录列表成功:', response?.data);
      if (response.data && Array.isArray(response?.data?.data)) {
        const dirs = response.data?.data.map((dir: any) => ({
          value: dir,
          label: dir
        }));
        
        // 如果没有目录，设置默认目录
        if (dirs.length === 0) {
          dirs.push({ value: "url_upload", label: "URL上传目录" });
        }
        
        setDirOptions(dirs);
        
        // 如果当前选中的目录不在列表中，选择第一个
        if (!dirs.find((dir: { value: string; }) => dir.value === selectedDir)) {
          setSelectedDir(dirs[0]?.value || "url_upload");
        }
      } else {
        console.error('获取目录列表失败:', response.data);
        // 如果接口返回格式不符合预期，设置默认目录
        setDirOptions([
          { value: "url_upload", label: "URL上传目录" },
          { value: "images", label: "图片目录" },
          { value: "temp", label: "临时目录" }
        ]);
      }
    } catch (error) {
      console.error('获取目录列表失败:', error);
      // 如果接口调用失败，设置默认目录
      setDirOptions([
        { value: "url_upload", label: "URL上传目录" },
        { value: "images", label: "图片目录" },
        { value: "temp", label: "临时目录" }
      ]);
    } finally {
      setIsLoadingDirs(false);
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
    setUrls("");
    setUploadProgress([]);
    setCurrentUploadIndex(0);
    setRandomPath(false);
    
    // 重置上传结果状态
    setUploadResult({
      totalCount: 0,
      successCount: 0,
      errorCount: 0,
      successUrls: [],
      errorUrls: [],
      completedAt: null
    });
    
    // 打开弹窗时获取目录列表
    fetchDirectoryList();
  };

  const handleCancel = () => {
    if (!isUploading) {
      setIsModalVisible(false);
      setUrls("");
      setUploadProgress([]);
      setRandomPath(false);
      setUploadResult({
        totalCount: 0,
        successCount: 0,
        errorCount: 0,
        successUrls: [],
        errorUrls: [],
        completedAt: null
      });
    } else {
      message.warning("请【手动刷新页面】来终止上传");
    }
  };

  const validateUrls = (urlText: string): string[] => {
    const lines = urlText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    if (lines.length === 0) {
      throw new Error("请输入至少一个URL");
    }
    
    if (lines.length > 500) {
      throw new Error("最多只能上传500个URL");
    }

    const validUrls: string[] = [];
    const invalidUrls: string[] = [];

    lines.forEach((line, index) => {
      try {
        const url = new URL(line);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          validUrls.push(line);
        } else {
          invalidUrls.push(`第${index + 1}行: ${line}`);
        }
      } catch {
        invalidUrls.push(`第${index + 1}行: ${line}`);
      }
    });

    if (invalidUrls.length > 0) {
      throw new Error(`以下URL格式无效:\n${invalidUrls.join('\n')}`);
    }

    return validUrls;
  };

  // 从URL中提取文件名，使用完整的path部分
  const extractFileNameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      
      // 如果pathname为空或者只有斜杠，使用域名作为文件名
      if (!pathname || pathname === '/') {
        return `${urlObj.hostname}_${Date.now()}`;
      }
      
      // 移除开头的斜杠，获取完整的path部分作为文件名
      let fileName = pathname.startsWith('/') ? pathname.substring(1) : pathname;
      
      // 如果没有文件扩展名，尝试从URL参数或查询字符串中获取
      if (!fileName.includes('.')) {
        // 检查是否有查询参数包含文件信息
        const searchParams = urlObj.searchParams;
        const fileParam = searchParams.get('file') || searchParams.get('name') || searchParams.get('filename');
        if (fileParam && fileParam.includes('.')) {
          fileName = fileParam;
        } else {
          // 如果还是没有扩展名，添加默认扩展名
          fileName = `${fileName}_${Date.now()}`;
        }
      }
      
      // 清理文件名中的特殊字符，但保留斜杠（用于路径分隔）
      fileName = fileName.replace(/[<>:"\\|?*]/g, '_');
      
      return fileName;
    } catch {
      // 如果URL解析失败，使用时间戳作为文件名
      return `url_upload_${Date.now()}`;
    }
  };

  const uploadSingleUrl = async (url: string, fileName: string, index: number): Promise<void> => {
    try {
      const formData = new FormData();
      formData.append('fromUrl', url);
      formData.append('fileName', fileName);
      formData.append('uid', getId() as any);
      formData.append('dir', selectedDir);
      formData.append('randomPath', randomPath.toString());

      await axios.post('/img/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => 
              prev.map((item, i) => 
                i === index 
                  ? { ...item, progress, status: 'uploading' as const }
                  : item
              )
            );
          }
        }
      });

      setUploadProgress(prev => 
        prev.map((item, i) => 
          i === index 
            ? { ...item, status: 'success' as const, progress: 100, message: '上传成功' }
            : item
        )
      );
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || '上传失败';
      
      // 记录失败的文件到全局状态
      addFailedFile(fileName, errorMessage);
      
      setUploadProgress(prev => 
        prev.map((item, i) => 
          i === index 
            ? { ...item, status: 'error' as const, message: errorMessage, progress: 0 }
            : item
        )
      );
      throw error;
    }
  };

  const handleUpload = async () => {
    try {
      const validUrls = validateUrls(urls);
      
      // 验证目录选择
      if (!selectedDir || selectedDir.trim() === '') {
        message.error('请选择上传目录');
        return;
      }
      
      setIsUploading(true);
      setUploadProgress(validUrls.map(url => ({
        url,
        status: 'pending' as const,
        progress: 0
      })));

      for (let i = 0; i < validUrls.length; i++) {
        setCurrentUploadIndex(i);
        
        // 更新当前URL状态为上传中
        setUploadProgress(prev => 
          prev.map((item, index) => 
            index === i 
              ? { ...item, status: 'uploading' as const }
              : item
          )
        );

        const url = validUrls[i];
        const fileName = extractFileNameFromUrl(url);
        
        try {
          await uploadSingleUrl(url, fileName, i);
          // 不在这里显示成功消息，让进度条显示状态
        } catch (error) {
          // 不在这里显示错误消息，让进度条显示状态
        }

        if (i < validUrls.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // 等待状态更新完成后再获取统计结果
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // 使用函数式更新确保获取最新状态
      setUploadProgress(prevProgress => {
        const successCount = prevProgress.filter(item => item.status === 'success').length;
        const errorCount = prevProgress.filter(item => item.status === 'error').length;
        const successUrls = prevProgress.filter(item => item.status === 'success').map(item => item.url);
        const errorUrls = prevProgress.filter(item => item.status === 'error').map(item => item.url);
        
        // 添加调试信息
        console.log('上传完成后的状态统计:', {
          total: prevProgress.length,
          success: successCount,
          error: errorCount,
          successUrls,
          errorUrls,
          allStatuses: prevProgress.map(item => ({ url: item.url, status: item.status }))
        });
        
        // 更新上传结果状态
        setUploadResult({
          totalCount: validUrls.length,
          successCount,
          errorCount,
          successUrls,
          errorUrls,
          completedAt: new Date()
        });
        
        // 如果有成功上传的文件，触发事件通知父组件重新加载历史数据
        if (successCount > 0) {
          eventBus.emit('urlUploadSuccess', {
            successCount,
            successUrls,
            dir: selectedDir
          });
        }
        
        // 显示最终统计结果
        if (errorCount === 0) {
          message.success(`所有${validUrls.length}个URL上传完成！`);
        } else {
          message.warning(`上传完成！成功: ${successCount}个，失败: ${errorCount}个`);
        }
        
        return prevProgress;
      });
    } catch (error: any) {
      message.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusColor = (status: UploadProgress['status']) => {
    switch (status) {
      case 'pending': return '#d9d9d9';
      case 'uploading': return '#1890ff';
      case 'success': return '#52c41a';
      case 'error': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getStatusText = (status: UploadProgress['status']) => {
    switch (status) {
      case 'pending': return '等待中';
      case 'uploading': return '上传中';
      case 'success': return '成功';
      case 'error': return '失败';
      default: return '未知';
    }
  };

  return (
    <>
      <Button size="small" type="primary" onClick={showModal}>
        点击进行 URL 上传
      </Button>
      
      <Modal
        title="批量URL上传"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            取消
          </Button>,
          <Button 
            key="upload" 
            type="primary" 
            loading={isUploading}
            disabled={!urls.trim() || !selectedDir.trim()}
            onClick={handleUpload}
          >
            {isUploading ? '上传中...' : '开始上传'}
          </Button>
        ]}
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text>请选择上传目录：</Text>
            <Select
              value={selectedDir}
              onChange={setSelectedDir}
              style={{ width: 200 }}
              disabled={isUploading || isLoadingDirs}
              loading={isLoadingDirs}
              placeholder={isLoadingDirs ? "加载中..." : "请选择目录"}
              size="small"
            >
              {dirOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            <Button
              size="small"
              icon="🔄"
              onClick={fetchDirectoryList}
              disabled={isLoadingDirs || isUploading}
              title="刷新目录列表"
            />
            {isLoadingDirs && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                正在加载目录列表...
              </Text>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text>随机路径设置：</Text>
            <Select
              value={randomPath}
              onChange={setRandomPath}
              style={{ width: 200 }}
              disabled={isUploading}
              size="small"
            >
              <Option value={false}>否（使用原始路径文件名）</Option>
              <Option value={true}>是（生成随机路径）</Option>
            </Select>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              选择"是"时，系统会为每个文件生成随机路径；选择"否"时，保持原始文件名
            </Text>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text>请输入要上传的URL，每行一个，最多500个：</Text>
        </div>
        
        <TextArea
          rows={8}
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          placeholder="请输入URL，每行一个，例如：&#10;https://example.com/image1.jpg&#10;https://example.com/image2.png&#10;https://example.com/image3.gif"
          disabled={isUploading}
          onPaste={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          style={{ marginBottom: 16 }}
        />

        {uploadProgress.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text strong>上传进度：</Text>
            <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
              {uploadProgress.map((item, index) => (
                <div key={index} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text 
                      style={{ 
                        color: getStatusColor(item.status),
                        fontSize: '12px',
                        maxWidth: '60%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.url}
                    </Text>
                    <Text style={{ fontSize: '12px', color: getStatusColor(item.status) }}>
                      {getStatusText(item.status)}
                    </Text>
                  </div>
                  
                  {item.status === 'uploading' && (
                    <Progress 
                      percent={item.progress} 
                      size="small" 
                      strokeColor={getStatusColor(item.status)}
                    />
                  )}
                  
                  {item.status === 'success' && (
                    <Progress 
                      percent={100} 
                      size="small" 
                      strokeColor={getStatusColor(item.status)}
                      status="success"
                    />
                  )}
                  
                  {item.status === 'error' && (
                    <Progress 
                      percent={0} 
                      size="small" 
                      strokeColor={getStatusColor(item.status)}
                      status="exception"
                    />
                  )}
                  
                  {item.message && (
                    <Text 
                      style={{ 
                        fontSize: '12px', 
                        color: item.status === 'success' ? '#52c41a' : '#ff4d4f' 
                      }}
                    >
                      {item.message}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 新增：上传结果展示区域 */}
        {uploadResult.completedAt && (
          <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f6f8fa', borderRadius: 6 }}>
            <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
              上传结果汇总
            </Text>
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>总数量：{uploadResult.totalCount}</Text>
                <Text style={{ color: '#52c41a' }}>成功：{uploadResult.successCount}</Text>
                <Text style={{ color: '#ff4d4f' }}>失败：{uploadResult.errorCount}</Text>
              </div>
              
              {uploadResult.successUrls.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ color: '#52c41a' }}>成功上传的URL：</Text>
                  <div style={{ marginTop: 4, maxHeight: 120, overflowY: 'auto' }}>
                    {uploadResult.successUrls.map((url, index) => (
                      <div key={index} style={{ fontSize: '12px', color: '#52c41a', marginBottom: 2 }}>
                        {url}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {uploadResult.errorUrls.length > 0 && (
                <div>
                  <Text strong style={{ color: '#ff4d4f' }}>上传失败的URL：</Text>
                  <div style={{ marginTop: 4, maxHeight: 120, overflowY: 'auto' }}>
                    {uploadResult.errorUrls.map((url, index) => (
                      <div key={index} style={{ fontSize: '12px', color: '#ff4d4f', marginBottom: 2 }}>
                        {url}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c' }}>
                完成时间：{uploadResult.completedAt.toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UrlUpload;